import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  Container,
  CircularProgress,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CelebrationIcon from "@mui/icons-material/Celebration";
import QrCodeIcon from "@mui/icons-material/QrCode";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { getEnv } from "../../envConfig";

const env = getEnv();

const MotionBox = motion(Box);

const VerificationSuccess = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const [verificationState, setVerificationState] = useState({
    loading: true,
    success: false,
    error: null,
    message: "",
  });

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setVerificationState({
          loading: false,
          success: false,
          error: "No verification token found",
          message:
            "Invalid verification link. Please check your email and try again.",
        });
        return;
      }

      try {
        const response = await axios.get(
          `${env.ENDPOINT}/api/restaurant/auth/verify-email?token=${encodeURIComponent(token)}`,
        );

        if (response.data.success) {
          setVerificationState({
            loading: false,
            success: true,
            error: null,
            message: response.data.message || "Email verified successfully",
          });
        } else {
          const apiMessage =
            response.data.message || response.data.error || "Unable to verify email";
          setVerificationState({
            loading: false,
            success: false,
            error: "Verification failed",
            message: apiMessage,
          });
        }
      } catch (error) {
        console.error("Email verification error:", error);
        const apiError =
          error.response?.data?.message || error.response?.data?.error || "";
        const alreadyVerified =
          typeof apiError === "string" &&
          /already verified/i.test(apiError);

        if (alreadyVerified) {
          setVerificationState({
            loading: false,
            success: true,
            error: null,
            message: apiError || "Your email is already verified. You can sign in.",
          });
          return;
        }

        setVerificationState({
          loading: false,
          success: false,
          error: apiError || error.message || "Verification failed",
          message:
            apiError ||
            "An error occurred during verification. The link may be expired or invalid.",
        });
      }
    };

    verifyEmail();
  }, [searchParams]);

  // Loading State
  if (verificationState.loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: "center" }}>
          <CircularProgress
            size={80}
            thickness={4}
            sx={{
              color: "white",
              mb: 3,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: 600,
              mb: 2,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Verifying Your Email...
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Please wait while we verify your email address
          </Typography>
        </Container>
      </Box>
    );
  }

  // Error State
  if (!verificationState.success && verificationState.error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #1a0000 0%, #3d0000 50%, #2b0000 100%)"
              : "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Floating Background Icons */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          {/* Floating Restaurant Icons - Dimmed */}
          <Box
            component={motion.div}
            animate={{
              y: [0, -25, 0],
              rotate: [0, 10, -10, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            sx={{
              position: "absolute",
              top: "15%",
              left: "8%",
              opacity: 0.1,
            }}
          >
            <QrCodeIcon sx={{ fontSize: 70, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, 30, 0],
              rotate: [0, -15, 15, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            sx={{
              position: "absolute",
              top: "20%",
              right: "10%",
              opacity: 0.1,
            }}
          >
            <RestaurantMenuIcon sx={{ fontSize: 68, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, 25, 0],
              rotate: [0, 15, -15, 0],
            }}
            transition={{
              duration: 11,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5,
            }}
            sx={{
              position: "absolute",
              bottom: "20%",
              left: "12%",
              opacity: 0.1,
            }}
          >
            <LocalDiningIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>

          <Box
            component={motion.div}
            animate={{
              y: [0, -28, 0],
              rotate: [0, 12, -12, 0],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.8,
            }}
            sx={{
              position: "absolute",
              bottom: "15%",
              right: "15%",
              opacity: 0.1,
            }}
          >
            <FastfoodIcon sx={{ fontSize: 66, color: "white" }} />
          </Box>
        </Box>

        <Container
          maxWidth="md"
          sx={{ position: "relative", zIndex: 1, py: 6 }}
        >
          {/* Error Icon with Animation */}
          <MotionBox
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.2,
            }}
            sx={{ textAlign: "center", mb: 3 }}
          >
            <Box
              sx={{
                display: "inline-flex",
                p: 4,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
                position: "relative",
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 100, color: "white" }} />

              {/* Warning badge */}
              <Box
                component={motion.div}
                animate={{
                  rotate: [0, -10, 10, -10, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                sx={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  background:
                    "linear-gradient(135deg, #ff5252 0%, #e53935 100%)",
                  borderRadius: "50%",
                  p: 1.5,
                  boxShadow: "0 8px 24px rgba(229, 57, 53, 0.6)",
                }}
              >
                <Typography sx={{ fontSize: 40 }}>⚠️</Typography>
              </Box>
            </Box>
          </MotionBox>

          {/* Error Message */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            sx={{ textAlign: "center", mb: 5 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: "white",
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                fontSize: { xs: "2.5rem", md: "4rem" },
              }}
            >
              Verification Failed
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "rgba(255, 255, 255, 0.95)",
                mb: 2,
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              }}
            >
              Unable to verify your email address
            </Typography>
            <Box
              sx={{
                maxWidth: 650,
                mx: "auto",
                p: 3,
                borderRadius: 2,
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255, 255, 255, 0.95)",
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                  fontWeight: 500,
                  lineHeight: 1.6,
                }}
              >
                {verificationState.message}
              </Typography>
            </Box>
          </MotionBox>

          {/* Action Buttons */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            sx={{ maxWidth: 500, mx: "auto" }}
          >
            <Stack spacing={2}>
              {/* <Button
                variant="contained"
                fullWidth
                size="large"
                endIcon={<RocketLaunchIcon />}
                onClick={() => navigate("/company-registration")}
                sx={{
                  py: 2.5,
                  fontSize: "1.2rem",
                  fontWeight: 800,
                  borderRadius: 3,
                  background: "rgba(255, 255, 255, 0.98)",
                  color: "#d32f2f",
                  boxShadow: "0 12px 40px rgba(255, 255, 255, 0.4)",
                  textTransform: "none",
                  border: "2px solid rgba(255, 255, 255, 0.5)",
                  "&:hover": {
                    background: "white",
                    transform: "translateY(-3px)",
                    boxShadow: "0 16px 48px rgba(255, 255, 255, 0.5)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Request New Link
              </Button> */}
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => navigate("/login")}
                sx={{
                  py: 2.5,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  borderRadius: 3,
                  background: "rgba(255, 255, 255, 0.98)",
                  borderWidth: "2px",
                  borderColor: "rgba(255, 255, 255, 0.6)",
                  color: "#d32f2f",
                  textTransform: "none",
                  "&:hover": {
                    borderWidth: "2px",
                    borderColor: "white",
                    background: "rgba(255, 255, 255, 0.1)",
                    transform: "translateY(-3px)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Go to Login
              </Button>
            </Stack>
          </MotionBox>
        </Container>
      </Box>
    );
  }

  // Success State (original component content)
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Floating Celebration Icons */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Party items falling top to bottom — one-time burst on page load */}
        {[
          {
            emoji: "🎉",
            left: "3%",
            size: 34,
            dur: 2.0,
            delay: 0.0,
            spin: 200,
          },
          {
            emoji: "🌟",
            left: "9%",
            size: 28,
            dur: 1.7,
            delay: 0.12,
            spin: -160,
          },
          {
            emoji: "🎊",
            left: "15%",
            size: 32,
            dur: 2.3,
            delay: 0.05,
            spin: 240,
          },
          {
            emoji: "✨",
            left: "22%",
            size: 24,
            dur: 1.5,
            delay: 0.3,
            spin: -200,
          },
          {
            emoji: "🎈",
            left: "29%",
            size: 36,
            dur: 2.1,
            delay: 0.08,
            spin: 180,
          },
          {
            emoji: "🎉",
            left: "36%",
            size: 30,
            dur: 1.8,
            delay: 0.2,
            spin: -220,
          },
          {
            emoji: "🌟",
            left: "43%",
            size: 26,
            dur: 2.4,
            delay: 0.02,
            spin: 260,
          },
          {
            emoji: "🎊",
            left: "50%",
            size: 32,
            dur: 1.6,
            delay: 0.35,
            spin: -180,
          },
          {
            emoji: "✨",
            left: "57%",
            size: 28,
            dur: 2.2,
            delay: 0.15,
            spin: 200,
          },
          {
            emoji: "🎈",
            left: "64%",
            size: 34,
            dur: 1.9,
            delay: 0.25,
            spin: -240,
          },
          {
            emoji: "🎉",
            left: "71%",
            size: 30,
            dur: 2.5,
            delay: 0.0,
            spin: 160,
          },
          {
            emoji: "🌟",
            left: "78%",
            size: 26,
            dur: 1.7,
            delay: 0.4,
            spin: -200,
          },
          {
            emoji: "🎊",
            left: "85%",
            size: 32,
            dur: 2.0,
            delay: 0.1,
            spin: 220,
          },
          {
            emoji: "✨",
            left: "91%",
            size: 28,
            dur: 1.5,
            delay: 0.28,
            spin: -180,
          },
          {
            emoji: "🎈",
            left: "97%",
            size: 36,
            dur: 2.3,
            delay: 0.06,
            spin: 200,
          },
          {
            emoji: "🎉",
            left: "12%",
            size: 24,
            dur: 1.8,
            delay: 0.45,
            spin: -160,
          },
          {
            emoji: "🌟",
            left: "33%",
            size: 30,
            dur: 2.6,
            delay: 0.18,
            spin: 240,
          },
          {
            emoji: "🎊",
            left: "55%",
            size: 28,
            dur: 1.6,
            delay: 0.5,
            spin: -200,
          },
          {
            emoji: "✨",
            left: "74%",
            size: 34,
            dur: 2.1,
            delay: 0.22,
            spin: 180,
          },
          {
            emoji: "🎈",
            left: "88%",
            size: 26,
            dur: 1.9,
            delay: 0.55,
            spin: -220,
          },
        ].map((item, i) => (
          <Box
            key={i}
            component={motion.div}
            initial={{ y: -60, opacity: 1, rotate: 0 }}
            animate={{
              y: "105vh",
              opacity: [1, 1, 1, 0.6, 0],
              rotate: item.spin,
            }}
            transition={{
              duration: item.dur,
              delay: item.delay,
              ease: "easeIn",
            }}
            sx={{
              position: "absolute",
              fontSize: item.size,
              left: item.left,
              top: 0,
              userSelect: "none",
              filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
            }}
          >
            {item.emoji}
          </Box>
        ))}

        {/* Floating Restaurant Icons */}
        <Box
          component={motion.div}
          animate={{
            y: [0, -25, 0],
            rotate: [0, 10, -10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            top: "15%",
            left: "8%",
            opacity: 0.15,
          }}
        >
          <QrCodeIcon sx={{ fontSize: 70, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, 30, 0],
            rotate: [0, -15, 15, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          sx={{
            position: "absolute",
            top: "20%",
            right: "10%",
            opacity: 0.15,
          }}
        >
          <RestaurantMenuIcon sx={{ fontSize: 68, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, 25, 0],
            rotate: [0, 15, -15, 0],
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5,
          }}
          sx={{
            position: "absolute",
            bottom: "20%",
            left: "12%",
            opacity: 0.15,
          }}
        >
          <LocalDiningIcon sx={{ fontSize: 64, color: "white" }} />
        </Box>

        <Box
          component={motion.div}
          animate={{
            y: [0, -28, 0],
            rotate: [0, 12, -12, 0],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.8,
          }}
          sx={{
            position: "absolute",
            bottom: "15%",
            right: "15%",
            opacity: 0.15,
          }}
        >
          <FastfoodIcon sx={{ fontSize: 66, color: "white" }} />
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, py: 6 }}>
        {/* Success Icon with Animation */}
        <MotionBox
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.2,
          }}
          sx={{ textAlign: "center", mb: 3 }}
        >
          <Box
            sx={{
              display: "inline-flex",
              p: 4,
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 20px 60px rgba(255, 255, 255, 0.3)",
              position: "relative",
            }}
          >
            <RocketLaunchIcon sx={{ fontSize: 100, color: "white" }} />

            {/* Celebration icon */}
            <Box
              component={motion.div}
              animate={{
                rotate: [0, 20, -20, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                borderRadius: "50%",
                p: 1.5,
                boxShadow: "0 8px 24px rgba(245, 87, 108, 0.6)",
              }}
            >
              <CelebrationIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
          </Box>
        </MotionBox>

        {/* Success Message */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          sx={{ textAlign: "center", mb: 5 }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: "white",
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
              fontSize: { xs: "2.5rem", md: "4rem" },
            }}
          >
            🎉 Congratulations! 🎉
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "rgba(255, 255, 255, 0.95)",
              mb: 2,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            {verificationState.message ||
              "Your email has been verified successfully!"}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              maxWidth: 650,
              mx: "auto",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            Your account is now fully activated and ready to use. You can now
            proceed to login and start managing your restaurant.
          </Typography>
        </MotionBox>

        {/* Action Button */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          sx={{ maxWidth: 500, mx: "auto" }}
        >
          <Button
            variant="contained"
            fullWidth
            size="large"
            endIcon={<RocketLaunchIcon />}
            onClick={() => navigate("/login")}
            sx={{
              py: 2.5,
              fontSize: "1.2rem",
              fontWeight: 800,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.98)",
              color: "#5b6fd6 !important",
              boxShadow: "0 12px 40px rgba(255, 255, 255, 0.4)",
              textTransform: "none",
              border: "2px solid rgba(255, 255, 255, 0.5)",
              "& .MuiSvgIcon-root": {
                color: "#5b6fd6 !important",
              },
              "&:hover": {
                background: "white",
                color: "#4c5fc7 !important",
                transform: "translateY(-3px)",
                boxShadow: "0 16px 48px rgba(255, 255, 255, 0.5)",
                "& .MuiSvgIcon-root": {
                  color: "#4c5fc7 !important",
                },
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Go to Login
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default VerificationSuccess;
